import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import * as moment from 'moment'
import { useState, useMemo, useCallback } from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { WarnAreasData } from '../../_types'
import {
  getMeasureValueSegmentsByCondition,
  InfluxSeriesMeasuresData,
  InfluxSeriesMeasuresDatum,
  TimeRangeColoredSegment,
} from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/sharedV2'
import React from 'react'
import { generateTimeRangeColoredArea } from '@mv-submodules/inplant-plantanalysis-fe-iblu/functions/sharedV2'
import { Workshift } from '@mv-submodules/inplant-plantanalysis-fe-iblu/types/workshift'
import {
  AdditionalChartLineData,
  UnitAxisConfig,
  AXIS_ID_PREFIX,
  formatTooltipValue,
  formatTooltipDisplayValue,
  buildUnitAxisConfigs,
  buildAxisIdMap,
  getYAxisProps,
} from './_utils'

// Re-export for external use
export type { AdditionalChartLineData }

export interface OwnProps {
  additionalChartLines: AdditionalChartLineData[]
  warnAreasData: WarnAreasData[]
  data: InfluxSeriesMeasuresData
  workShift: Workshift
}

export interface OwnState {
  showTooltip: boolean
  tooltipData: any
}
export const COLORS = {
  0: '#26c0c7',
  1: '#5151d3',
  2: '#e68619',
  3: '#d83790',
  4: '#2680eb',
  5: '#9bec54',
  6: '#268e6c',
  7: '#dfbf00',
  8: '#6f38b1',
  9: '#9089fa',
  10: '#47e26f',
  11: '#cb6f10',
}
type Props = OwnProps & WithTranslation

const WARN_AREA_DEFAULT_COLOR = '#FF9893'
const PAUSES_AREA_DEFAULT_COLOR = '#4cff8d'

const nullTooltipContent = ({ active }: { active?: boolean }) => {
  if (active) {
    return <div className="null-tooltip-content" />
  }

  return null
}

const getLineColor = (index: number): string => {
  return COLORS[index as keyof typeof COLORS] || '#000000'
}

const GenericLineGraph = (props: Props) => {
  const { additionalChartLines, data, t, warnAreasData, workShift } = props
  const [showTooltip, setShowTooltip] = useState(false)
  const [tooltipData, setTooltipData] = useState<any>(null)

  const unitAxisConfigs = useMemo<UnitAxisConfig[]>(() => buildUnitAxisConfigs(additionalChartLines), [
    additionalChartLines,
  ])

  const axisIdByUnit = useMemo(() => buildAxisIdMap(additionalChartLines, unitAxisConfigs), [
    unitAxisConfigs,
    additionalChartLines,
  ])

  const defaultAxisId = unitAxisConfigs[0]?.id ?? `${AXIS_ID_PREFIX}0`
  const showYAxis = unitAxisConfigs.length > 0

  const pausesInFilteredData = useMemo(() => {
    if (!workShift?.pauses?.length || !data.length) {
      return null
    }
    const rangeStart = data[0].time
    const rangeEnd = data[data.length - 1].time
    return workShift.pauses.filter(pause => pause.start <= rangeEnd && pause.end >= rangeStart)
  }, [workShift, data])

  const warnAreasComponents = useMemo(() => {
    const _warnAreasSegments = warnAreasData.reduce<TimeRangeColoredSegment[]>((acc, wa) => {
      const segments = getMeasureValueSegmentsByCondition(data, wa.measure, wa.fn)
      acc.push(
        ...segments.map(segment => ({
          start: segment.start,
          end: segment.end,
          color: wa.color ?? WARN_AREA_DEFAULT_COLOR,
        }))
      )
      return acc
    }, [])
    return generateTimeRangeColoredArea({
      segments: _warnAreasSegments,
      options: { opacity: 0.5, yAxisId: defaultAxisId },
    })
  }, [warnAreasData, data, defaultAxisId])

  const pauseAreasComponents = useMemo(() => {
    if (!pausesInFilteredData?.length) {
      return null
    }
    const rangeStart = data[0].time
    const rangeEnd = data[data.length - 1].time
    return generateTimeRangeColoredArea({
      segments: pausesInFilteredData.map(pause => ({
        start: Math.max(rangeStart, pause.start),
        end: Math.min(rangeEnd, pause.end),
        color: PAUSES_AREA_DEFAULT_COLOR,
      })),
      options: { opacity: 0.5, yAxisId: defaultAxisId },
    })
  }, [data, defaultAxisId, pausesInFilteredData])

  const handleMouseEnter = useCallback(() => {
    setShowTooltip(true)
  }, [])

  const handleMouseMove = useCallback(
    (args: any) => {
      if (args?.activeLabel) {
        const curData = data.find((e: InfluxSeriesMeasuresDatum) => e.time === args.activeLabel)

        if (curData) {
          setShowTooltip(true)
          setTooltipData({
            time: curData.time,
            ...additionalChartLines?.reduce((acc, curr) => {
              const value = curData[curr.slug]
              const formattedValue = formatTooltipValue(value, curr.type, curr.unitOfMeasurement, t)
              return {
                ...acc,
                [curr.slug]: formattedValue,
              }
            }, {}),
          })
        }
      }
    },
    [additionalChartLines, data, t]
  )

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false)
    setTooltipData(null)
  }, [])

  return (
    <div className="row">
      <div className="col-md-10" style={{ padding: showYAxis ? '0 20px' : '0 80px' }}>
        <ResponsiveContainer width="100%" height={400}>
          <ComposedChart
            width={700}
            height={400}
            data={data}
            margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
            onMouseEnter={handleMouseEnter}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
            barGap={0}
            barCategoryGap={0}
          >
            <XAxis
              hide={true}
              dataKey="time"
              label={{ value: '', position: 'insideBottomRight', offset: 0 }}
              height={30}
              axisLine={false}
              tickLine={false}
              domain={['dataMin', 'dataMax']}
              tickFormatter={timeStr => moment(timeStr, 'X').format('HH:mm')}
              type={'number'}
            />

            {showYAxis &&
              unitAxisConfigs.map(config => <YAxis key={config.id} {...getYAxisProps(config, t)} />)}

            <CartesianGrid stroke="#f5f5f5" />

            {warnAreasComponents}
            {pauseAreasComponents}

            <Tooltip content={nullTooltipContent} />
            {additionalChartLines.map((ac, index) => (
              <Line
                key={ac.slug}
                isAnimationActive={false}
                dataKey={ac.slug}
                stroke={ac.color ?? getLineColor(index)}
                dot={false}
                strokeWidth={2}
                type="stepAfter"
                yAxisId={axisIdByUnit.get(ac.unitOfMeasurement)}
                strokeOpacity={0.8}
              />
            ))}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="col-md-2">
        {showTooltip && tooltipData !== null && (
          <div>
            <ul className="side-tooltip" style={{ marginBottom: 16 }}>
              <li>
                <strong>{t('plantAnalysis.labels.time')}</strong>
                <br />
                <span>{moment(tooltipData.time, 'X').format('HH:mm')}</span>
              </li>
              {additionalChartLines.map((ac, index) => {
                const label = ac.label ?? t(`plantAnalysis.labels.${ac.slug}`)
                const color = ac.color ?? getLineColor(index)
                const displayValue = formatTooltipDisplayValue(tooltipData[ac.slug], ac.type, ac.unitOfMeasurement)
                return (
                  <li key={ac.slug}>
                    <strong>{label}</strong>
                    <br />
                    <span className="label-color label-color-line" style={{ backgroundColor: color }} />
                    <span>{displayValue}</span>
                  </li>
                )
              })}
            </ul>
            <ul className="side-tooltip" style={{ marginTop: 16 }}>
              {pausesInFilteredData && (
                <li style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong>{t('plantAnalysis.labels.pauses')}</strong>
                  <span
                    className="label-color label-color-line"
                    style={{ backgroundColor: PAUSES_AREA_DEFAULT_COLOR }}
                  />
                </li>
              )}
              {warnAreasData.map(wa => (
                <li key={wa.slug} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <strong>{t(`plantAnalysis.labels.${wa.slug}`)}</strong>
                  <span
                    className="label-color label-color-line"
                    style={{ backgroundColor: wa.color ?? WARN_AREA_DEFAULT_COLOR }}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

export default withTranslation()(GenericLineGraph)
